import React, { useState } from 'react';
import { Caption, H2, H3 } from '~/shared/Typography';
import { Box, VStack, HStack, Pressable, Actionsheet } from 'native-base';
import { i18nFn, SubscriptionPlanDTO } from '@springbig/shared';
import { useHomePageStyles } from '~/shared/styles';
import { CardTitle } from '~/pages/homePage/components/CardTitle';
import PlanBenefits from '~/pages/subscriptions/components/PlanBenefits';
import { PlanSchedule } from '~/pages/subscriptions/components/PlanSchedule';
import { SpIcon } from '~/shared/Icon';
import { faLock, faLockOpen, faGifts } from '@fortawesome/free-solid-svg-icons';
import { CardBottomLink } from '~/pages/homePage/components/CardBottomLink';

type SubscriptionsProps = {
    data: Array<SubscriptionPlanDTO>;
    t: i18nFn;
};

export const Subscriptions = ({
    data,
    t,
}: SubscriptionsProps) => {

    const { primary_color, pressable_text, icon } = useHomePageStyles();
    const [
        isOpenBenefits,
        setIsOpenBenefits
    ] = useState<boolean>(false);
    const [
        selectedPlan,
        setSelectedPlan
    ] = useState<SubscriptionPlanDTO>(null);

    const sortedPlans = data
        .sort((a, b) => {

            if (a.subscription && !b.subscription) {
                return -1;
            }

            if (!a.subscription && b.subscription) {
                return 1;
            }

            return 0;
        });

    const onSelectPlan = (plan: SubscriptionPlanDTO): void => {

        setSelectedPlan(plan);
        setIsOpenBenefits(true);
    };

    const onClosePlanel = (): void => {

        setSelectedPlan(null);
        setIsOpenBenefits(false);
    };

    return (
        <>
            <VStack space={4}>
                <CardTitle
                    title={t('home_page.subscriptions.title')}
                    icon={'faGem'}
                />
                <VStack space={2}>
                    { [...sortedPlans]
                        .splice(0, 3)
                        .map((plan, index) =>

                            <HStack
                                key={index}
                                space={4}
                                bg={primary_color}
                                rounded={'2xl'}
                                alignItems={'center'}
                                py={2}
                                px={4}
                            >
                                <Box flex={1}>
                                    <SpIcon
                                        icon={faGifts}
                                        color={icon}
                                        size={42}
                                    />
                                </Box>
                                <VStack
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                    flex={3}
                                >
                                    <HStack alignItems={'center'} space={2}>
                                        <H2
                                            color={pressable_text}
                                            fontWeight={300}
                                            my={0}
                                        >
                                            { plan.name }
                                        </H2>
                                        <SpIcon
                                            icon={plan.subscription ? faLockOpen : faLock}
                                            color={icon}
                                            size={14}
                                        />
                                    </HStack>
                                    <PlanSchedule
                                        plan={plan}
                                        fontSize={10}
                                        fontWeight={200}
                                    />
                                </VStack>
                                <Pressable onPress={() => onSelectPlan(plan)} flex={1}>
                                    <Caption textAlign={'center'}>
                                        { t('home_page.subscriptions.see_benefits') }
                                    </Caption>
                                </Pressable>
                            </HStack>
                        )}
                </VStack>
                { data.length > 3 &&
                    <CardBottomLink t={t} link={'subscriptions'} text={'home_page.view_more'} />
                }
            </VStack>
            { isOpenBenefits &&
                <Actionsheet isOpen={isOpenBenefits} onClose={onClosePlanel}>
                    <Actionsheet.Content
                        zIndex={9999}
                        overflow='auto'
                        w={'clamp(320px, 100vw, 480px)'}
                        alignSelf='center'
                    >
                        <H3 alignSelf={'center'}>
                            { t('home_page.subscriptions.benefits') }
                        </H3>
                        <Box py={10} px={4} w={'100%'}>
                            { selectedPlan &&
                                <PlanBenefits plan={selectedPlan} />
                            }
                        </Box>
                    </Actionsheet.Content>
                </Actionsheet>
            }
        </>
    );
};
